import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, ArrowLeft } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useContactModal } from '../context/ContactModalContext';
import { Link } from 'react-router-dom';

// Reusable page layout for service/industry pages
const ServicePage = ({ title, subtitle, description, benefits, positions, ctaText }) => {
  const { language } = useLanguage();
  const { openContactModal } = useContactModal();

  const backText = {
    sk: 'Späť na hlavnú stránku',
    en: 'Back to homepage',
    de: 'Zurück zur Startseite'
  };

  const contactText = {
    sk: 'Kontaktujte nás',
    en: 'Contact Us',
    de: 'Kontaktieren Sie uns'
  };

  const benefitsTitle = {
    sk: 'Výhody spolupráce',
    en: 'Benefits of cooperation',
    de: 'Vorteile der Zusammenarbeit'
  };

  const positionsTitle = {
    sk: 'Pozície, ktoré obsadzujeme',
    en: 'Positions we fill',
    de: 'Positionen, die wir besetzen'
  };

  return (
    <div className="min-h-screen bg-[#FDF8F0]">
      {/* Header */}
      <div className="bg-[#E8A238] pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-6">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-[#131132]/70 hover:text-[#131132] mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {backText[language]}
          </Link>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#131132] mb-4">
            {title}
          </h1>
          
          <p className="text-xl md:text-2xl text-[#131132]/70 max-w-3xl">
            {subtitle}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-6 py-16">
        {/* Description */}
        <div className="mb-16">
          <p className="text-lg text-[#131132]/70 leading-relaxed max-w-4xl whitespace-pre-line">
            {description}
          </p>
        </div>

        {/* Benefits */}
        {benefits && benefits.length > 0 && (
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-[#131132] mb-8">{benefitsTitle[language]}</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="flex items-start gap-4 p-6 bg-white rounded-2xl shadow-sm"
                >
                  <CheckCircle2 className="w-6 h-6 text-[#E8A238] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-[#131132] mb-2">{benefit.title}</h3>
                    <p className="text-[#131132]/60">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Positions */}
        {positions && positions.length > 0 && (
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-[#131132] mb-8">{positionsTitle[language]}</h2>
            <div className="flex flex-wrap gap-3">
              {positions.map((position, index) => (
                <span
                  key={index}
                  className="px-5 py-3 bg-[#131132] text-white rounded-full font-medium"
                >
                  {position}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="bg-[#131132] rounded-3xl p-10 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {ctaText || contactText[language]}
          </h2>
          <p className="text-white/60 mb-8 max-w-2xl mx-auto">
            {language === 'sk' && 'Máte záujem o spoluprácu? Kontaktujte nás a pripravíme vám nezáväznú ponuku.'}
            {language === 'en' && 'Interested in cooperation? Contact us and we will prepare a non-binding offer for you.'}
            {language === 'de' && 'Interesse an einer Zusammenarbeit? Kontaktieren Sie uns und wir erstellen Ihnen ein unverbindliches Angebot.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.button
              onClick={openContactModal}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#E8A238] text-[#131132] font-semibold rounded-xl hover:bg-[#D97706] transition-colors"
              data-testid="service-contact-button"
            >
              {contactText[language]}
              <ArrowRight className="w-5 h-5" />
            </motion.button>
            <motion.a
              href="tel:+421949303240"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center justify-center gap-3 px-8 py-4 border-2 border-white text-white font-semibold rounded-xl hover:bg-white hover:text-[#131132] transition-all"
            >
              +421 949 303 240
            </motion.a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServicePage;
